import React, { useContext, useCallback, useMemo, useState } from "react";
import { Flex, Box } from "reflexbox";
import { getProducts } from "../../requests/productManagementRequests";
import { useQuery } from "../Hooks";
import { Columns } from "./columns";
import Table from "../UI/Table";
import UserContext from "../../context/user/UserContext";
import ProductCard from "./ProductCard";
import ConfiguratorContext from "../../context/configurator/ConfiguratorContext";
import Card from "../UI/Card";
import _ from "lodash";
import { Body, Helper } from "../UI/Text";
import PageView from "./PageView";
import formatter from "../../modules/formatter";
import { useTheme } from "styled-components";

const ProductCatalog = ({
  inline = false,
  onProductClick,
  isLoading: _isLoading = false,
  pageView = false,
}) => {
  const theme = useTheme();
  const {
    loggedIn,
    pid,
    isLoading: userLoading,
    activeUser,
  } = useContext(UserContext);
  const { quote } = useContext(ConfiguratorContext);

  const { data, refetchQuery, isLoading } = useQuery({
    queryID: inline ? "quickConfig" : pageView ? "productPageView" : "products",
    request: getProducts,
    forcedFilters: inline ? { limit: 6 } : undefined,
    queryVariables: inline
      ? {
          isQuickConfig: true,
          isNonConfigurable: false,
          pid,
          gid: quote?.groupId,
        }
      : {
          isQuickConfig: loggedIn ? undefined : true,
          pid,
          gid:
            pageView && activeUser?.roleLevel !== 0
              ? activeUser?.groupId
              : quote?.groupId,
        },
    attributes:
      pageView || !inline
        ? [
            "ProductNumber",
            "Name",
            "ID",
            "ApplicationInstance",
            "ApplicationName",
            "isNonConfigurable",
            "isQuickConfig",
            "isNonConfigurable",
            "HeaderId",
            "img",
            "base64Image",
            "ImageUrl",
            "defaultPrice",
            "Description",
            "HeaderId",
            "Overview",
            "quantityInStock",
            "priceCode",
            "Discount",
            "RelatedItem",
          ]
        : ["ID", "ImageUrl"],
    skip: !loggedIn || _isLoading || userLoading,
  });

  const [showPreview, setShowPreview] = useState(false);

  const gridData = useMemo(() => {
    if (_.isArray(data?.rows)) {
      if (pageView) {
        return data.rows.map((product) => {
          const image =
            product?.img || product?.base64Image || product?.ImageUrl;
          return {
            ...product,
            image,
            title: product?.Name,

            iconName: !!image ? undefined : "faBox",
            elements: [
              <Helper
                key="Product Description"
                lines={2}
                ellipsis
                my="1rem"
                center
              >
                {product?.Description}
              </Helper>,
              <Body key="Default Price" align right>
                ${formatter.currency(product?.defaultPrice)}
              </Body>,
            ],
          };
        });
      }
      return data.rows.map((product) => (
        <ProductCard
          key={`${product.ID}|catalog`}
          inline={inline}
          onProductClick={onProductClick}
          product={product}
        />
      ));
    }

    return null;
  }, [data, inline, onProductClick, pageView]);

  const component = useMemo(() => {
    if (inline) return gridData;

    if (pageView)
      return (
        <PageView
          data={data}
          gridData={gridData}
          refetchQuery={refetchQuery}
          setShowPreview={setShowPreview}
          showPreview={showPreview}
          isLoading={isLoading}
        />
      );

    return (
      <Flex
        width={
          inline
            ? "auto"
            : ["100vw", "100vw", "100vw", "100vw", "100vw", "100vw", "1136px"]
        }
        height="80vh"
        p={0}
        style={{ zIndex: "9", position: "relative" }}
      >
        <Card width={1} overflowY="scroll">
          <Box mt="-1rem" mb="-2rem" width={1}>
            <Table
              hideCreate
              backButtonIcon="faXmark"
              backButtonColor={theme.danger}
              createButtonText={null}
              isLoading={isLoading}
              title="Product Catalog"
              columns={Columns(onProductClick)}
              gridSize="medium"
              gridData={gridData}
              data={data?.rows || []}
              customGrid
              totalCount={data?.count}
              hideSearch={inline}
              hideProperties={inline}
              onCardClick={(row) => onProductClick(row.id)}
            />
          </Box>
        </Card>
      </Flex>
    );
  }, [
    inline,
    gridData,
    pageView,
    data,
    refetchQuery,
    showPreview,
    isLoading,
    theme.danger,
    onProductClick,
  ]);

  return component;
};

export default ProductCatalog;
